import React, { useEffect, useState, useMemo, useRef } from 'react';
import { 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow, 
    Paper, 
    Typography, 
    Box,
    TablePagination,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Chip,
    Tabs,
    Tab,
    Card,
    CardContent,
    Grid,
    Button,
    TextField as MuiTextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useApi } from '../hooks/useApi';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import moment from 'moment-timezone';
import ReactECharts from 'echarts-for-react';

const RelaisLogPage = () => {
    const [logs, setLogs] = useState([]);
    const [overrides, setOverrides] = useState([]);
    const [combinedLogs, setCombinedLogs] = useState([]);
    const [auditLogs, setAuditLogs] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [error, setError] = useState(null);
    const [sortField, setSortField] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState('DESC');
    const [tabValue, setTabValue] = useState(0); // Start met gecombineerde tab (nu eerste tab)
    const [startDate, setStartDate] = useState(() => moment().subtract(2, 'days').startOf('day'));
    const [endDate, setEndDate] = useState(() => moment().endOf('day'));
    const { get } = useApi();

    // Helper functies
    const formatDuration = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = Math.round((minutes % 60) * 10) / 10;
        return `${hours}u ${mins}m`;
    };

    const formatDurationWhole = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = Math.round(minutes % 60);
        return `${hours}u ${mins}m`;
    };

    const normalizeReason = (reason) => {
        if (reason === null || reason === undefined || reason === '') {
            return 'forced on';
        }
        const normalized = reason.toString().toLowerCase().replace(/\s+/g, ' ').trim();
        return normalized;
    };

    // Kleuren voor verschillende statussen
    const COLORS = {
        // Werkelijke status kleuren
        'planning_on': '#a5d6a7',      // Licht groen - planning aan
        'planning_off': '#ef9a9a',     // Licht rood - planning uit  
        'override_on': '#4caf50',      // Helder groen - override aan
        'override_off': '#f44336',     // Helder rood - override uit
        
        // Vlaggetje kleuren voor wijzigingen
        'flag_override': '#ff9800',    // Oranje vlaggetje voor override wijziging
        'flag_auto': '#2196f3',        // Blauw vlaggetje voor terug naar auto
        
        // Legacy kleuren (voor backwards compatibility)
        'automatic schedule': '#a5d6a7',
        'forced on': '#4caf50',
        'forced off': '#f44336',
        'on': '#4caf50',
        'off': '#ef9a9a',
        'auto': '#2196f3'
    };

    const fetchLogs = async () => {
        try {
            setError(null);
            const offset = page * rowsPerPage;
            const limit = rowsPerPage;
            
            const response = await get(
                `/relais-log?filter={}&range=[${offset},${offset + limit - 1}]&sort=["${sortField}","${sortOrder}"]`
            );
            
            if (response.data && response.data.data) {
                setLogs(response.data.data);
                setTotalCount(response.data.total);
                if (response.data.metadata) {
                    setTotalPages(response.data.metadata.total_pages);
                }
            } else {
                console.error('Ongeldige data ontvangen van de server:', response.data);
                setError('Ongeldige data ontvangen van de server');
            }
        } catch (error) {
            console.error('Fout bij ophalen relais logs:', error);
            setError('Fout bij ophalen relais logs');
        }
    };

    const fetchOverrides = async () => {
        try {
            setError(null);
            const offset = page * rowsPerPage;
            const limit = rowsPerPage;
            
            const response = await get(
                `/override?filter={}&range=[${offset},${offset + limit - 1}]&sort=["${sortField}","${sortOrder}"]`
            );
            
            if (response.data && response.data.data) {
                setOverrides(response.data.data);
            } else {
                console.error('Ongeldige override data ontvangen van de server:', response.data);
                setError('Ongeldige override data ontvangen van de server');
            }
        } catch (error) {
            console.error('Fout bij ophalen override logs:', error);
            setError('Fout bij ophalen override logs');
        }
    };

    const fetchCombinedData = async () => {
        try {
            setError(null);
            const offset = page * rowsPerPage;
            const limit = rowsPerPage;
            
            // Gebruik datum filters
            const startFilter = startDate.toISOString();
            const endFilter = endDate.toISOString();
            
            // Haal zowel relais logs als overrides op met datum filtering
            const [relaisResponse, overrideResponse] = await Promise.all([
                get(`/relais-log?filter={"timestamp":{"gte":"${startFilter}","lte":"${endFilter}"}}&range=[0,999]&sort=["timestamp","DESC"]`),
                get(`/override?filter={"timestamp":{"gte":"${startFilter}","lte":"${endFilter}"}}&range=[0,999]&sort=["timestamp","DESC"]`)
            ]);
            
            const relaisLogs = relaisResponse.data?.data || [];
            const overrideLogs = overrideResponse.data?.data || [];
            
            // Combineer en sorteer op timestamp
            const combined = [
                ...relaisLogs.map(log => ({ ...log, type: 'relais' })),
                ...overrideLogs.map(override => ({ ...override, type: 'override' }))
            ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            
            // Pas paginering toe
            const startIndex = offset;
            const endIndex = startIndex + limit;
            const paginatedData = combined.slice(startIndex, endIndex);
            
            setCombinedLogs(paginatedData);
            setTotalCount(combined.length);
            
        } catch (error) {
            console.error('Fout bij ophalen gecombineerde data:', error);
            setError('Fout bij ophalen gecombineerde data');
        }
    };

    const fetchAuditLogs = async () => {
        try {
            setError(null);
            const offset = page * rowsPerPage;
            const limit = rowsPerPage;
            
            // Gebruik datum filters voor auditlogs
            const startFilter = startDate.toISOString();
            const endFilter = endDate.toISOString();
            
            const response = await get(
                `/audit-logs/?limit=${limit}&offset=${offset}&start_date=${startFilter}&end_date=${endFilter}`
            );
            
            if (response.data) {
                setAuditLogs(response.data);
                // Voor auditlogs hebben we mogelijk geen total count, dus schatten we op basis van de huidige page
                setTotalCount(response.data.length === limit ? (page + 2) * limit : (page * limit) + response.data.length);
            } else {
                console.error('Ongeldige auditlog data ontvangen van de server:', response.data);
                setError('Ongeldige auditlog data ontvangen van de server');
            }
        } catch (error) {
            console.error('Fout bij ophalen audit logs:', error);
            setError('Fout bij ophalen audit logs');
        }
    };

    useEffect(() => {
        if (tabValue === 0) {
            fetchCombinedData();
        } else if (tabValue === 1) {
            fetchLogs();
        } else if (tabValue === 2) {
            fetchOverrides();
        } else if (tabValue === 3) {
            fetchAuditLogs();
        }
    }, [page, rowsPerPage, sortField, sortOrder, tabValue, startDate, endDate]);

    const handleChangePage = (_event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSort = (field) => {
        if (field === sortField) {
            setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortField(field);
            setSortOrder('ASC');
        }
    };

    const handleTabChange = (_event, newValue) => {
        setTabValue(newValue);
        setPage(0);
    };

    // Bereken KPI's voor gecombineerde data
    const kpis = useMemo(() => {
        if (!combinedLogs || combinedLogs.length === 0) {
            return {
                totalTime: 0,
                runtime: 0,
                runtimePercentage: 0,
                startTime: null,
                endTime: null,
                reasonStats: {},
                switchCount: 0,
                avgOnDuration: 0,
                maxOnDuration: 0,
                forcedOverrideCount: 0,
                overrideCount: 0
            };
        }

        // Sorteer op timestamp
        const sortedData = [...combinedLogs].sort((a, b) => 
            moment(a.timestamp).valueOf() - moment(b.timestamp).valueOf()
        );

        const startTime = moment(sortedData[0].timestamp);
        
        // Voor eindtijd: gebruik huidige tijd als laatste record van vandaag is
        const lastRecord = sortedData[sortedData.length - 1];
        const now = moment();
        const isLastRecordToday = moment(lastRecord.timestamp).isSame(now, 'day');
        const endTime = isLastRecordToday ? now : moment(lastRecord.timestamp);
        
        const totalMinutes = endTime.diff(startTime, 'minutes');

        let runtimeMinutes = 0;
        const reasonStats = {};
        let switchCount = 0;
        let onPeriods = [];
        let currentOnStart = null;
        let forcedOverrideCount = 0;
        let overrideCount = 0;

        // Tel override acties
        overrideCount = sortedData.filter(item => item.type === 'override').length;

        // Bereken runtime alleen voor relais logs
        const relaisLogs = sortedData.filter(item => item.type === 'relais');
        
        for (let i = 0; i < relaisLogs.length - 1; i++) {
            const current = relaisLogs[i];
            const next = relaisLogs[i + 1];
            
            // Tel schakelingen
            if (current.status !== next.status) {
                switchCount++;
            }

            // Tel forced overrides
            const currentReason = normalizeReason(current.reason);
            if (currentReason === 'forced on' || currentReason === 'forced off') {
                forcedOverrideCount++;
            }
            
            if (current.status === 'on') {
                if (!currentOnStart) {
                    currentOnStart = moment(current.timestamp);
                }
                const duration = moment(next.timestamp).diff(moment(current.timestamp), 'minutes');
                runtimeMinutes += duration;
                
                if (!reasonStats[current.reason]) {
                    reasonStats[current.reason] = 0;
                }
                reasonStats[current.reason] += duration;
            } else if (currentOnStart) {
                // Einde van een on-periode
                const periodDuration = moment(current.timestamp).diff(currentOnStart, 'minutes');
                onPeriods.push(periodDuration);
                currentOnStart = null;
            }
        }

        // Voeg laatste periode toe als status 'on' is
        if (relaisLogs.length > 0) {
            const lastRecord = relaisLogs[relaisLogs.length - 1];
            if (lastRecord.status === 'on') {
                const duration = moment(endTime).diff(moment(lastRecord.timestamp), 'minutes');
                runtimeMinutes += duration;
                
                if (!reasonStats[lastRecord.reason]) {
                    reasonStats[lastRecord.reason] = 0;
                }
                reasonStats[lastRecord.reason] += duration;

                if (currentOnStart) {
                    const periodDuration = moment(endTime).diff(currentOnStart, 'minutes');
                    onPeriods.push(periodDuration);
                }
            }
        }

        // Bereken gemiddelde en maximale on-duur
        const avgOnDuration = onPeriods.length > 0 ? onPeriods.reduce((a, b) => a + b, 0) / onPeriods.length : 0;
        const maxOnDuration = onPeriods.length > 0 ? Math.max(...onPeriods) : 0;

        return {
            totalTime: totalMinutes,
            runtime: runtimeMinutes,
            runtimePercentage: totalMinutes > 0 ? (runtimeMinutes / totalMinutes) * 100 : 0,
            startTime,
            endTime,
            reasonStats,
            switchCount,
            avgOnDuration,
            maxOnDuration,
            forcedOverrideCount,
            overrideCount
        };
    }, [combinedLogs]);

    // Datum filter handlers
    const handleDateChange = (newDate, isStart) => {
        if (!newDate) return;
        
        if (isStart) {
            setStartDate(newDate);
        } else {
            setEndDate(newDate);
        }
        
        // Reset pagination when filters change
        setPage(0);
    };

    const handleResetDates = () => {
        const newStart = moment().subtract(2, 'days').startOf('day');
        const newEnd = moment().endOf('day');
        setStartDate(newStart);
        setEndDate(newEnd);
        setPage(0);
    };

    const formatDateTime = (dateString) => {
        try {
            // Converteer UTC timestamp naar CET (Central European Time)
            // Backend slaat altijd op in UTC, frontend toont in CET
            const utcTime = moment.utc(dateString);
            const cetTime = utcTime.tz('Europe/Amsterdam');
            
            // Formateer naar Nederlandse datum/tijd
            return cetTime.format('DD MMMM YYYY HH:mm:ss');
        } catch (error) {
            console.error('Fout bij formatteren datum:', error);
            return dateString;
        }
    };

    const getStatusColor = (status, state, type) => {
        if (type === 'relais') {
            return status === 'on' ? 'success' : 'default';
        } else {
            // Voor overrides
            switch (state) {
                case 'on':
                    return 'success';
                case 'off':
                    return 'error';
                case 'auto':
                    return 'info';
                default:
                    return 'default';
            }
        }
    };

    const renderTable = (data, isOverride = false, isCombined = false, isAuditLog = false) => (
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell 
                        style={{ cursor: 'pointer' }} 
                        onClick={() => handleSort(isAuditLog ? 'created_at' : 'timestamp')}
                    >
                        <Box display="flex" alignItems="center">
                            Tijdstip
                            {sortField === (isAuditLog ? 'created_at' : 'timestamp') && (
                                <span style={{ marginLeft: 4 }}>
                                    {sortOrder === 'ASC' ? '↑' : '↓'}
                                </span>
                            )}
                        </Box>
                    </TableCell>
                    {isAuditLog ? (
                        <>
                            <TableCell>Gebruiker</TableCell>
                            <TableCell>Actie</TableCell>
                            <TableCell>Details</TableCell>
                            <TableCell>IP Adres</TableCell>
                        </>
                    ) : (
                        <>
                            {isCombined && <TableCell>Type</TableCell>}
                            <TableCell 
                                style={{ cursor: 'pointer' }} 
                                onClick={() => handleSort(isOverride ? 'state' : 'status')}
                            >
                                <Box display="flex" alignItems="center">
                                    Status
                                    {sortField === (isOverride ? 'state' : 'status') && (
                                        <span style={{ marginLeft: 4 }}>
                                            {sortOrder === 'ASC' ? '↑' : '↓'}
                                        </span>
                                    )}
                                </Box>
                            </TableCell>
                            <TableCell>Reden</TableCell>
                            {isOverride && <TableCell>Gebruiker</TableCell>}
                        </>
                    )}
                </TableRow>
            </TableHead>
            <TableBody>
                {data.map((row, index) => (
                    <TableRow key={isAuditLog ? row.id : `${row.id}-${index}`}>
                        <TableCell>
                            {formatDateTime(isAuditLog ? row.created_at : row.timestamp)}
                        </TableCell>
                        {isAuditLog ? (
                            <>
                                <TableCell>
                                    {row.user_name ? (
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {row.user_name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {row.user_email}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color="textSecondary">
                                            Systeem
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Chip 
                                        label={row.action} 
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {row.details || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="textSecondary">
                                        {row.ip_address || '-'}
                                    </Typography>
                                </TableCell>
                            </>
                        ) : (
                            <>
                                {isCombined && (
                                    <TableCell>
                                        <Chip 
                                            label={row.type === 'relais' ? 'Relais' : 'Override'} 
                                            size="small"
                                            color={row.type === 'relais' ? 'default' : 'secondary'}
                                        />
                                    </TableCell>
                                )}
                                <TableCell>
                                    <Chip 
                                        label={isOverride ? row.state : row.status} 
                                        size="small"
                                        style={{
                                            backgroundColor: getStatusColor(
                                                isOverride ? row.state : row.status, 
                                                row.state, 
                                                row.type || (isOverride ? 'override' : 'relais')
                                            ),
                                            color: '#000'
                                        }}
                                    />
                                </TableCell>
                                <TableCell>{row.reason || '-'}</TableCell>
                                {isOverride && (
                                    <TableCell>
                                        {row.user_name ? (
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {row.user_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {row.user_email}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">
                                                Onbekend
                                            </Typography>
                                        )}
                                    </TableCell>
                                )}
                            </>
                        )}
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );

    const getCurrentData = () => {
        switch (tabValue) {
            case 0:
                return combinedLogs;
            case 1:
                return logs;
            case 2:
                return overrides;
            case 3:
                return auditLogs;
            default:
                return logs;
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h4">
                        Elektrolizer Log Geschiedenis
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DatePicker
                            label="Startdatum"
                            value={startDate}
                            onChange={(newValue) => handleDateChange(newValue, true)}
                            renderInput={(params) => <MuiTextField {...params} size="small" sx={{ minWidth: 150 }} />}
                            maxDate={endDate}
                        />
                        <DatePicker
                            label="Einddatum"
                            value={endDate}
                            onChange={(newValue) => handleDateChange(newValue, false)}
                            renderInput={(params) => <MuiTextField {...params} size="small" sx={{ minWidth: 150 }} />}
                            minDate={startDate}
                            maxDate={moment()}
                        />
                        <Button variant="outlined" onClick={handleResetDates} size="small">
                            Reset
                        </Button>
                        <Tooltip title="Ververs">
                            <IconButton onClick={() => {
                                if (tabValue === 0) fetchCombinedData();
                                else if (tabValue === 1) fetchLogs();
                                else if (tabValue === 2) fetchOverrides();
                                else if (tabValue === 3) fetchAuditLogs();
                            }}>
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange}>
                        <Tab label="Gecombineerd" />
                        <Tab label="Relais Log" />
                        <Tab label="Override Log" />
                        <Tab label="Audit Log" />
                    </Tabs>
                </Box>

                {/* KPI's */}
                {tabValue === 0 && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Statistieken
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h6" color="textSecondary">Periode</Typography>
                                        <Typography variant="h4">
                                            {kpis.startTime?.format('DD/MM')} - {kpis.endTime?.format('DD/MM')}
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {kpis.startTime?.format('HH:mm')} - {kpis.endTime?.format('HH:mm')}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h6" color="textSecondary">Runtime</Typography>
                                        <Typography variant="h4">{formatDuration(kpis.runtime)}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            {kpis.runtimePercentage.toFixed(1)}% van totaal {formatDuration(kpis.totalTime)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h6" color="textSecondary">Schakelingen</Typography>
                                        <Typography variant="h4">{kpis.switchCount}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Totaal status wijzigingen
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h6" color="textSecondary">Override Acties</Typography>
                                        <Typography variant="h4">{kpis.overrideCount}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Totaal override wijzigingen
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h6" color="textSecondary">Gem. Aan Duur</Typography>
                                        <Typography variant="h4">{formatDurationWhole(kpis.avgOnDuration)}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Gemiddelde aan-periode
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h6" color="textSecondary">Max Aan Duur</Typography>
                                        <Typography variant="h4">{formatDuration(kpis.maxOnDuration)}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Langste aan-periode
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Paper sx={{ p: 2, textAlign: 'center' }}>
                                        <Typography variant="h6" color="textSecondary">Forced Overrides</Typography>
                                        <Typography variant="h4">{kpis.forcedOverrideCount}</Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Handmatige acties
                                        </Typography>
                                    </Paper>
                                </Grid>
                                {Object.entries(kpis.reasonStats).map(([reason, minutes]) => (
                                    <Grid item xs={12} md={3} key={reason}>
                                        <Paper sx={{ p: 2, textAlign: 'center' }}>
                                            <Typography variant="h6" color="textSecondary">{reason}</Typography>
                                            <Typography variant="h4">{formatDuration(minutes)}</Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {((minutes / kpis.totalTime) * 100).toFixed(1)}% van totaal
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </CardContent>
                    </Card>
                )}

                {/* Timeline Grafiek */}
                {tabValue === 0 && combinedLogs.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Tijdlijn
                            </Typography>
                            
                            {/* Kleuren Legenda */}
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Legenda:
                                </Typography>
                                
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                    {/* Werkelijke Status Balken */}
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            Werkelijke Status (Balken):
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ 
                                                    width: 16, 
                                                    height: 16, 
                                                    backgroundColor: COLORS.planning_on,
                                                    border: '1px solid #ccc'
                                                }} />
                                                <Typography variant="body2">Planning Aan</Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ 
                                                    width: 16, 
                                                    height: 16, 
                                                    backgroundColor: COLORS.planning_off,
                                                    border: '1px solid #ccc'
                                                }} />
                                                <Typography variant="body2">Planning Uit</Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ 
                                                    width: 16, 
                                                    height: 16, 
                                                    backgroundColor: COLORS.override_on,
                                                    border: '1px solid #ccc'
                                                }} />
                                                <Typography variant="body2">Override Aan</Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ 
                                                    width: 16, 
                                                    height: 16, 
                                                    backgroundColor: COLORS.override_off,
                                                    border: '1px solid #ccc'
                                                }} />
                                                <Typography variant="body2">Override Uit</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    
                                    {/* Vlaggetjes voor Wijzigingen */}
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                            Wijzigingen (Vlaggetjes):
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ 
                                                    width: 0, 
                                                    height: 0, 
                                                    borderLeft: '8px solid transparent',
                                                    borderRight: '8px solid transparent',
                                                    borderBottom: `16px solid ${COLORS.flag_override}`,
                                                }} />
                                                <Typography variant="body2">Override Wissel</Typography>
                                            </Box>
                                            
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Box sx={{ 
                                                    width: 0, 
                                                    height: 0, 
                                                    borderLeft: '8px solid transparent',
                                                    borderRight: '8px solid transparent',
                                                    borderBottom: `16px solid ${COLORS.flag_auto}`,
                                                }} />
                                                <Typography variant="body2">Terug naar Auto</Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                            
                            <Box sx={{ height: 400 }}>
                                <ReactECharts
                                    option={{
                                        tooltip: {
                                            trigger: 'item',
                                            formatter: (params) => {
                                                // Veilige check voor params en data
                                                if (!params || !params.data) {
                                                    return '';
                                                }
                                                
                                                const item = params.data;
                                                
                                                // Voor vlaggetjes (scatter points)
                                                if (params.seriesType === 'scatter') {
                                                    if (!item.value || !Array.isArray(item.value) || item.value.length === 0) {
                                                        return '';
                                                    }
                                                    const timestamp = moment(item.value[0]).format('DD-MM-YYYY HH:mm:ss');
                                                    return `
                                                        <div style="font-weight: bold; margin-bottom: 5px;">${item.name || 'Onbekend'}</div>
                                                        <div>Tijdstip: ${timestamp}</div>
                                                    `;
                                                }
                                                
                                                // Voor balken (custom series)
                                                if (!item.value || !Array.isArray(item.value) || item.value.length < 2) {
                                                    return '';
                                                }
                                                
                                                const start = moment(item.value[0]).format('DD-MM-YYYY HH:mm:ss');
                                                const end = moment(item.value[1]).format('DD-MM-YYYY HH:mm:ss');
                                                const duration = moment.duration(moment(item.value[1]).diff(moment(item.value[0])));
                                                const hours = Math.floor(duration.asHours());
                                                const minutes = duration.minutes();
                                                
                                                return `
                                                    <div style="font-weight: bold; margin-bottom: 5px;">${item.name || 'Onbekend'}</div>
                                                    <div style="margin-bottom: 3px;">Start: ${start}</div>
                                                    <div style="margin-bottom: 3px;">Eind: ${end}</div>
                                                    <div>Duur: ${hours}u ${minutes}m</div>
                                                `;
                                            }
                                        },
                                                                                 grid: {
                                             left: '3%',
                                             right: '4%',
                                             bottom: '15%',
                                             top: '5%',
                                             containLabel: true
                                         },
                                         xAxis: {
                                             type: 'time',
                                             axisLabel: {
                                                 formatter: (value) => {
                                                     const date = moment(value);
                                                     if (date.hours() === 0 && date.minutes() === 0) {
                                                         return date.format('DD-MM 00:00');
                                                     }
                                                     return date.format('HH:mm');
                                                 },
                                                 rotate: 45
                                             },
                                             splitLine: {
                                                 show: true,
                                                 lineStyle: {
                                                     type: 'dashed',
                                                     color: '#ddd'
                                                 }
                                             },
                                             axisPointer: {
                                                 show: true,
                                                 type: 'line',
                                                 lineStyle: {
                                                     color: '#888',
                                                     width: 1,
                                                     type: 'dashed'
                                                 }
                                             },
                                             markLine: {
                                                 symbol: 'none',
                                                 lineStyle: {
                                                     color: '#444',
                                                     width: 2,
                                                     type: 'solid',
                                                     opacity: 0.5
                                                 },
                                                 data: (() => {
                                                     // Bepaal de range van de data
                                                     if (!combinedLogs || combinedLogs.length === 0) return [];
                                                     const allTimestamps = combinedLogs.map(item => moment(item.timestamp));
                                                     const min = moment.min(allTimestamps);
                                                     const max = moment.max(allTimestamps);
                                                     const lines = [];
                                                     let current = min.clone().startOf('day').add(1, 'day');
                                                     while (current.isBefore(max)) {
                                                         lines.push({ xAxis: current.valueOf() });
                                                         current.add(1, 'day');
                                                     }
                                                     return lines;
                                                 })()
                                             }
                                         },
                                         yAxis: {
                                             type: 'category',
                                             data: ['Elektrolizer Status'],
                                             axisLabel: {
                                                 show: true
                                             }
                                         },
                                         dataZoom: [{
                                             type: 'slider',
                                             show: true,
                                             xAxisIndex: [0],
                                             orient: 'horizontal',
                                      
                                             start: 0,
                                             end: 100,
                                             height: 20,
                                             bottom: 10,
                                             borderColor: 'transparent',
                                             backgroundColor: '#f5f5f5',
                                             handleStyle: {
                                                 color: '#666',
                                                 borderColor: '#666'
                                             },
                                             handleSize: 10,
                                             moveHandleSize: 10,
                                             selectedDataBackground: {
                                                 lineStyle: {
                                                     color: '#666',
                                                     type: 'solid',
                                                     width: 2,
                                                     cap: 'square',
                                                 },
                                                 areaStyle: {
                                                     color: '#666',
                                                     opacity: 0.3
                                                 }
                                             },
                                             emphasis: {
                                                 handleStyle: {
                                                     color: '#000',
                                                     borderColor: '#000'
                                                 }
                                             },
                                             filterMode: 'none',
                                             throttle: 100,
                                             zoomLock: false
                                         }],
                                         series: [
                                             // Serie 1: Werkelijke status balken (relais data)
                                             {
                                                 type: 'custom',
                                                 name: 'Werkelijke Status',
                                                 renderItem: (params, api) => {
                                                     const categoryIndex = api.value(2);
                                                     const start = api.coord([api.value(0), categoryIndex]);
                                                     const end = api.coord([api.value(1), categoryIndex]);
                                                     const height = api.size([0, 1])[1] * 0.4;
                                                     
                                                     return {
                                                         type: 'rect',
                                                         shape: {
                                                             x: start[0],
                                                             y: start[1] - height / 2,
                                                             width: Math.max(end[0] - start[0], 2),
                                                             height: height
                                                         },
                                                         style: api.style()
                                                     };
                                                 },
                                                 encode: {
                                                     x: [0, 1],
                                                     y: 2
                                                 },
                                                 data: (() => {
                                                     // Maak een complete timeline van alle status wijzigingen
                                                     const allEvents = combinedLogs
                                                         .map(item => ({
                                                             timestamp: moment(item.timestamp),
                                                             type: item.type,
                                                             status: item.status,
                                                             state: item.state,
                                                             reason: item.reason,
                                                             originalItem: item
                                                         }))
                                                         .sort((a, b) => a.timestamp.valueOf() - b.timestamp.valueOf());
                                                     
                                                     if (allEvents.length === 0) return [];
                                                     
                                                     // Bouw segmenten op basis van werkelijke status wijzigingen
                                                     const segments = [];
                                                     let currentRelaisStatus = null;
                                                     let currentMode = 'auto'; // auto, on, off
                                                     
                                                     for (let i = 0; i < allEvents.length; i++) {
                                                         const event = allEvents[i];
                                                         const nextEvent = allEvents[i + 1];
                                                         
                                                         let segmentStart = event.timestamp;
                                                         let segmentEnd;
                                                         
                                                         if (nextEvent) {
                                                             segmentEnd = nextEvent.timestamp;
                                                         } else {
                                                             // Laatste event: gebruik huidige tijd als het vandaag is
                                                             const now = moment();
                                                             const isToday = event.timestamp.isSame(now, 'day');
                                                             segmentEnd = isToday ? now : event.timestamp.clone().add(30, 'minutes');
                                                         }
                                                         
                                                         // Update status op basis van event type
                                                         if (event.type === 'relais') {
                                                             currentRelaisStatus = event.status;
                                                         } else if (event.type === 'override') {
                                                             currentMode = event.state;
                                                         }
                                                         
                                                         // Bepaal effectieve status en kleur
                                                         let effectiveStatus, displayType, effectiveReason, color;
                                                         
                                                         if (event.type === 'relais') {
                                                             effectiveStatus = event.status;
                                                             
                                                             // Bepaal of dit een forced actie is
                                                             const isForced = event.reason && (
                                                                 event.reason.includes('forced') || 
                                                                 event.reason.includes('manual')
                                                             );
                                                             
                                                             if (isForced) {
                                                                 displayType = 'Override';
                                                                 effectiveReason = event.reason;
                                                                 color = effectiveStatus === 'on' ? COLORS.override_on : COLORS.override_off;
                                                             } else {
                                                                 displayType = 'Planning';
                                                                 effectiveReason = event.reason || 'automatic schedule';
                                                                 color = effectiveStatus === 'on' ? COLORS.planning_on : COLORS.planning_off;
                                                             }
                                                         } else {
                                                             // Override event - bepaal wat de status wordt
                                                             if (event.state === 'auto') {
                                                                 // Override naar auto: gebruik laatste relais status maar als planning
                                                                 effectiveStatus = currentRelaisStatus;
                                                                 displayType = 'Planning';
                                                                 effectiveReason = 'automatic schedule';
                                                                 color = effectiveStatus === 'on' ? COLORS.planning_on : COLORS.planning_off;
                                                             } else {
                                                                 // Override naar specifieke status
                                                                 effectiveStatus = event.state;
                                                                 displayType = 'Override';
                                                                 effectiveReason = `forced ${event.state}`;
                                                                 color = effectiveStatus === 'on' ? COLORS.override_on : COLORS.override_off;
                                                             }
                                                         }
                                                         
                                                         // Voeg segment toe als het een werkelijke periode heeft
                                                         if (segmentEnd.isAfter(segmentStart)) {
                                                             segments.push({
                                                                 name: `${displayType} ${effectiveStatus} (${effectiveReason})`,
                                                                 value: [
                                                                     segmentStart.valueOf(),
                                                                     segmentEnd.valueOf(),
                                                                     0
                                                                 ],
                                                                 itemStyle: { color }
                                                             });
                                                         }
                                                     }
                                                     
                                                     return segments;
                                                 })()
                                             },
                                             
                                             // Serie 2: Override wijzigingen vlaggetjes
                                             {
                                                 type: 'scatter',
                                                 name: 'Override Wijzigingen',
                                                 symbolSize: 12,
                                                 symbol: 'triangle',
                                                 data: (() => {
                                                     // Filter alleen override logs
                                                     return combinedLogs
                                                         .filter(item => item.type === 'override')
                                                         .map(item => {
                                                             const color = item.state === 'auto' 
                                                                 ? COLORS.flag_auto 
                                                                 : COLORS.flag_override;
                                                             
                                                             return {
                                                                 name: `Override → ${item.state}`,
                                                                 value: [moment(item.timestamp).valueOf(), 0],
                                                                 itemStyle: { color },
                                                                 symbolSize: 15
                                                             };
                                                         });
                                                 })()
                                             }
                                         ]
                                    }}
                                    style={{ height: '100%', width: '100%' }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                )}

            <TableContainer component={Paper}>
                {tabValue === 0 && renderTable(combinedLogs, false, true)}
                {tabValue === 1 && renderTable(logs, false, false)}
                {tabValue === 2 && renderTable(overrides, true, false)}
                {tabValue === 3 && renderTable(auditLogs, false, false, true)}
                
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rijen per pagina:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} van ${count}`}
                />
            </TableContainer>

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                </Alert>
            </Snackbar>
            </Box>
        </LocalizationProvider>
    );
};

export default RelaisLogPage; 